import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { School, Public, TrendingUp, People } from '@mui/icons-material';

const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    { icon: <School />, title: 'Expert Guidance', description: 'Professional counselors' },
    { icon: <Public />, title: 'Global Reach', description: '7 office locations' },
    { icon: <TrendingUp />, title: 'High Success Rate', description: '98% placement rate' },
    { icon: <People />, title: '500+ Students', description: 'Successfully placed' },
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToAbout = () => {
    const element = document.getElementById('about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #e2e8f0 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        pt: { xs: 8, md: 0 }, // Add top padding for mobile to account for fixed header
      }}
    >
      {/* Background Overlay for better text readability */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(248, 250, 252, 0.5) 50%, rgba(226, 232, 240, 0.3) 100%)',
          zIndex: 1,
        }}
      />

      {/* Animated Background Elements */}
      <motion.div
        style={{ y }}
        className="absolute inset-0"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '10%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(44, 90, 160, 0.1)',
            filter: 'blur(40px)',
            zIndex: 2,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '60%',
            right: '10%',
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'rgba(255, 107, 53, 0.1)',
            filter: 'blur(60px)',
            zIndex: 2,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            left: '50%',
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(44, 90, 160, 0.05)',
            filter: 'blur(30px)',
            zIndex: 2,
          }}
        />
      </motion.div>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3 }}>
        <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >

              <Typography
                variant="h1"
                sx={{
                  color: '#1e3a5f',
                  mb: { xs: 2, md: 3 },
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '4rem' },
                  fontWeight: 800,
                  lineHeight: 1.1,
                }}
              >
                Welcome to{' '}
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(135deg, #2c5aa0 0%, #ff6b35 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Future Forwards
                </Box>
              </Typography>
              
              <Typography
                variant="h5"
                sx={{
                  color: '#64748b',
                  mb: { xs: 3, md: 4 },
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' },
                }}
              >
                ONE-STOP DESTINATION FOR ALL YOUR STUDY ABROAD NEEDS
              </Typography>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 1, md: 2 }, 
                  flexWrap: 'wrap',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' }
                }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={scrollToContact}
                    sx={{
                      background: 'linear-gradient(135deg, #2c5aa0 0%, #1e3a5f 100%)',
                      borderRadius: '25px',
                      px: { xs: 3, md: 4 },
                      py: { xs: 1.2, md: 1.5 },
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: theme.shadows[4],
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: theme.shadows[8],
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Get Started Today
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={scrollToAbout}
                    sx={{
                      borderColor: '#2c5aa0',
                      color: '#2c5aa0',
                      borderRadius: '25px',
                      px: { xs: 3, md: 4 },
                      py: { xs: 1.2, md: 1.5 },
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#2c5aa0',
                        color: 'white',
                        transform: 'translateY(-3px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Learn More
                  </Button>
                </Box>
              </motion.div>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{ position: 'relative' }}
            >
              {/* Main Girl Image */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1, delay: 0.4 }}
                whileHover={{ scale: 1.02 }}
                style={{
                  position: 'relative',
                  zIndex: 2,
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(44, 90, 160, 0.2)',
                    height: { xs: '300px', sm: '400px', md: '600px' },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(135deg, rgba(44, 90, 160, 0.1) 0%, rgba(255, 107, 53, 0.1) 100%)',
                      zIndex: 1,
                    }
                  }}
                >
                  <img
                    src="/girl_photo.png"
                    alt="Student studying abroad"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      position: 'relative',
                      zIndex: 2,
                    }}
                  />
                </Box>
              </motion.div>

              {/* Floating Feature Cards around the image */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: 1, 
                  y: [0, -10, 0],
                }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.6,
                  y: {
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }
                }}
                style={{
                  position: 'absolute',
                  top: '10%',
                  right: '-10%',
                  zIndex: 3,
                  display: isMobile ? 'none' : 'block',
                }}
              >
                <Card
                  sx={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(44, 90, 160, 0.2)',
                    borderRadius: 3,
                    p: 2,
                    boxShadow: '0 10px 30px rgba(44, 90, 160, 0.15)',
                    minWidth: 120,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <School sx={{ color: '#2c5aa0', fontSize: '1.5rem' }} />
                    <Typography variant="body2" sx={{ color: '#1e3a5f', fontWeight: 600, fontSize: '0.8rem' }}>
                      Expert Guidance
                    </Typography>
                  </Box>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: 1, 
                  y: [0, -8, 0],
                }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.8,
                  y: {
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                  }
                }}
                style={{
                  position: 'absolute',
                  bottom: '20%',
                  left: '-15%',
                  zIndex: 3,
                  display: isMobile ? 'none' : 'block',
                }}
              >
                <Card
                  sx={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 107, 53, 0.2)',
                    borderRadius: 3,
                    p: 2,
                    boxShadow: '0 10px 30px rgba(255, 107, 53, 0.15)',
                    minWidth: 120,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Public sx={{ color: '#ff6b35', fontSize: '1.5rem' }} />
                    <Typography variant="body2" sx={{ color: '#1e3a5f', fontWeight: 600, fontSize: '0.8rem' }}>
                      Global Reach
                    </Typography>
                  </Box>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: 1, 
                  y: [0, -12, 0],
                }}
                transition={{ 
                  duration: 0.8, 
                  delay: 1.0,
                  y: {
                    duration: 3.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.5
                  }
                }}
                style={{
                  position: 'absolute',
                  top: '60%',
                  right: '-5%',
                  zIndex: 3,
                  display: isMobile ? 'none' : 'block',
                }}
              >
                <Card
                  sx={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(44, 90, 160, 0.2)',
                    borderRadius: 3,
                    p: 2,
                    boxShadow: '0 10px 30px rgba(44, 90, 160, 0.15)',
                    minWidth: 120,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp sx={{ color: '#2c5aa0', fontSize: '1.5rem' }} />
                    <Typography variant="body2" sx={{ color: '#1e3a5f', fontWeight: 600, fontSize: '0.8rem' }}>
                      98% Success
                    </Typography>
                  </Box>
                </Card>
              </motion.div>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;
